import type { UserPrivate } from '@repo/types/users';

import { usePostPasswordReset } from '@repo/api/auth/usePostPasswordReset';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { LockIcon } from 'lucide-react';
import { useEffect } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the ResetPassword component.
 * @property {UserPrivate | null} [user] - The user whose password will be reset.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface ResetPasswordProps {
  user?: UserPrivate | null;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function ResetPassword(resetPasswordProps: ResetPasswordProps) {
  const { user, open, onOpenChange } = resetPasswordProps;
  const { mutateAsync, reset } = usePostPasswordReset();

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = () => {
    if (user) {
      mutateAsync({
        email: user.email,
        authToken: null,
      }).catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
      onClose();
    }
  };

  const onClose = () => {
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <LockIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>Czy chcesz resetowa hasło ?</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>Resetuj hasło dla wybranego/nych użytkowników.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-3 overflow-auto px-4'}>
            <p className={'text-sx text-text-secondary'}>
              Czy na pewno chcesz resetować hasło użytkownika <b>{user?.name + ' ' + user?.surname}</b> ?
            </p>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
